import { useEffect, useState, useCallback } from "react";
import { auth } from "../lib/firebase";
import { StaffMember, StaffAccessConfig, UserRole } from "../types";
import {
  subscribeToSettings,
  updateStaffAccess,
  bulkUpdateStaffAccess,
  DEFAULT_STAFF_ACCESS,
} from "./settingsService";

// Standard fallback staff members matching known team (Katie, Meredith, Jill, Mark)
export const DEFAULT_STAFF: StaffMember[] = [
  {
    id: "staff-katie",
    displayName: "Katie",
    email: "katie@mlconnections.com",
    department: "Proposals",
    hasAccess: true,
    role: "lead",
  },
  {
    id: "staff-meredith",
    displayName: "Meredith",
    email: "meredith@mlconnections.com",
    department: "Proposals",
    hasAccess: true,
    role: "lead",
  },
  {
    id: "staff-jill",
    displayName: "Jill",
    email: "jill@mlconnections.com",
    department: "Executive",
    hasAccess: true,
    role: "lead",
  },
  {
    id: "staff-mark",
    displayName: "Mark",
    email: "mark@mlconnections.com",
    department: "Technology",
    hasAccess: true,
    role: "admin",
  },
];

let cachedStaff: StaffMember[] | null = null;

export function _resetStaffCacheForTesting(): void {
  cachedStaff = null;
}

/**
 * Merges raw Staff HQ members with Proposal HQ staffAccess configuration
 */
export function annotateStaffWithAccess(
  members: StaffMember[],
  staffAccess: Record<string, StaffAccessConfig> = DEFAULT_STAFF_ACCESS,
): StaffMember[] {
  return members.map((member) => {
    const emailKey = (member.email || "").trim().toLowerCase();
    const config = staffAccess[emailKey] || DEFAULT_STAFF_ACCESS[emailKey];

    if (config) {
      return {
        ...member,
        hasAccess: config.hasAccess,
        role: config.role,
      };
    }

    return {
      ...member,
      hasAccess: false,
      role: "contributor" as UserRole,
    };
  });
}

/**
 * Filters staff members to only those authorized for RFP Proposal HQ
 */
export function getAuthorizedStaff(
  allStaff: StaffMember[],
  staffAccess?: Record<string, StaffAccessConfig>,
): StaffMember[] {
  const annotated = staffAccess ? annotateStaffWithAccess(allStaff, staffAccess) : allStaff;
  return annotated.filter((m) => m.hasAccess === true);
}

/**
 * Filters staff members to Proposal Leads and Administrators
 */
export function getProposalLeads(
  allStaff: StaffMember[],
  staffAccess?: Record<string, StaffAccessConfig>,
): StaffMember[] {
  const authorized = getAuthorizedStaff(allStaff, staffAccess);
  return authorized.filter((m) => m.role === "admin" || m.role === "lead");
}

/**
 * Fetches active staff members from the phq_syncFromHr Cloud Function endpoint
 */
export async function fetchActiveStaff(forceRefresh = false): Promise<StaffMember[]> {
  if (!forceRefresh && cachedStaff && cachedStaff.length > 0) {
    return cachedStaff;
  }

  const currentUser = auth.currentUser;
  if (!currentUser) {
    return annotateStaffWithAccess(DEFAULT_STAFF);
  }

  try {
    const token = await currentUser.getIdToken();
    const endpoint =
      import.meta.env.VITE_SYNC_HR_ENDPOINT ||
      (import.meta.env.VITE_USE_EMULATORS === "true"
        ? "http://127.0.0.1:5001/in-house-dev-mlc/us-central1/phq_syncFromHr"
        : "https://us-central1-in-house-dev-mlc.cloudfunctions.net/phq_syncFromHr");

    const response = await fetch(endpoint, {
      method: "GET",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
    });

    if (!response.ok) {
      console.warn(`Staff sync returned status ${response.status}. Using local directory cache.`);
      return annotateStaffWithAccess(DEFAULT_STAFF);
    }

    const data = await response.json();
    if (data && Array.isArray(data.activeStaff) && data.activeStaff.length > 0) {
      cachedStaff = data.activeStaff;
      return data.activeStaff;
    }

    return annotateStaffWithAccess(DEFAULT_STAFF);
  } catch (err) {
    console.warn(
      "Failed to reach phq_syncFromHr endpoint; falling back to team directory cache:",
      err,
    );
    return annotateStaffWithAccess(DEFAULT_STAFF);
  }
}

export interface UseStaffDirectoryOptions {
  rfpOnly?: boolean;
  leadOnly?: boolean;
}

/**
 * React Hook providing the reactive list of staff members for proposal assignment dropdowns.
 * Defaults to rfpOnly: true to filter to only staff involved in the RFP response process.
 */
export function useStaffDirectory(options?: UseStaffDirectoryOptions): {
  staff: StaffMember[];
  allStaff: StaffMember[];
  loading: boolean;
  error: string | null;
} {
  const rfpOnly = options?.rfpOnly !== false;
  const leadOnly = options?.leadOnly === true;

  const [rawStaff, setRawStaff] = useState<StaffMember[]>(cachedStaff || DEFAULT_STAFF);
  const [staffAccess, setStaffAccess] =
    useState<Record<string, StaffAccessConfig>>(DEFAULT_STAFF_ACCESS);
  const [loading, setLoading] = useState<boolean>(!cachedStaff);
  const [error, setError] = useState<string | null>(null);

  // Subscribe to real-time staff access settings from Firestore
  useEffect(() => {
    const unsubscribe = subscribeToSettings((settings) => {
      if (settings.staffAccess) {
        setStaffAccess(settings.staffAccess);
      }
    });
    return () => unsubscribe();
  }, []);

  // Fetch staff directory from Staff HQ endpoint
  useEffect(() => {
    let isMounted = true;

    async function loadStaff() {
      try {
        const members = await fetchActiveStaff();
        if (isMounted) {
          setRawStaff(members);
          setLoading(false);
        }
      } catch (err) {
        if (isMounted) {
          setError(err instanceof Error ? err.message : "Error loading staff");
          setLoading(false);
        }
      }
    }

    loadStaff();

    return () => {
      isMounted = false;
    };
  }, []);

  // Merge access configuration into staff items
  const annotatedAllStaff = annotateStaffWithAccess(rawStaff, staffAccess);

  let filteredStaff = annotatedAllStaff;
  if (leadOnly) {
    filteredStaff = getProposalLeads(rawStaff, staffAccess);
  } else if (rfpOnly) {
    filteredStaff = getAuthorizedStaff(rawStaff, staffAccess);
  }

  // Ensure default fallback staff are always present if list would otherwise be empty
  const effectiveStaff =
    filteredStaff.length > 0 ? filteredStaff : annotateStaffWithAccess(DEFAULT_STAFF, staffAccess);

  return {
    staff: effectiveStaff,
    allStaff: annotatedAllStaff,
    loading,
    error,
  };
}

/**
 * Dedicated hook for the /settings/staff management view
 */
export function useStaffManagementDirectory(): {
  allStaff: StaffMember[];
  activeRfpStaff: StaffMember[];
  leads: StaffMember[];
  loading: boolean;
  refreshing: boolean;
  error: string | null;
  refreshStaff: () => Promise<void>;
  toggleAccess: (email: string, hasAccess: boolean) => Promise<void>;
  changeRole: (email: string, role: UserRole) => Promise<void>;
  bulkUpdate: (
    emails: string[],
    updates: { hasAccess?: boolean; role?: UserRole },
  ) => Promise<void>;
} {
  const [rawStaff, setRawStaff] = useState<StaffMember[]>(cachedStaff || DEFAULT_STAFF);
  const [staffAccess, setStaffAccess] =
    useState<Record<string, StaffAccessConfig>>(DEFAULT_STAFF_ACCESS);
  const [loading, setLoading] = useState<boolean>(!cachedStaff);
  const [refreshing, setRefreshing] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const unsubscribe = subscribeToSettings((settings) => {
      if (settings.staffAccess) {
        setStaffAccess(settings.staffAccess);
      }
    });
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    let isMounted = true;

    async function initialLoad() {
      try {
        const members = await fetchActiveStaff();
        if (isMounted) {
          setRawStaff(members);
          setLoading(false);
        }
      } catch (err) {
        if (isMounted) {
          setError(err instanceof Error ? err.message : "Error loading staff directory");
          setLoading(false);
        }
      }
    }

    initialLoad();

    return () => {
      isMounted = false;
    };
  }, []);

  const refreshStaff = useCallback(async () => {
    setRefreshing(true);
    try {
      const members = await fetchActiveStaff(true);
      setRawStaff(members);
      setLoading(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Error loading staff directory");
      setLoading(false);
    } finally {
      setRefreshing(false);
    }
  }, []);

  const toggleAccess = useCallback(async (email: string, hasAccess: boolean) => {
    const currentUser = auth.currentUser;
    await updateStaffAccess(
      email,
      { hasAccess },
      {
        displayName: currentUser?.displayName || undefined,
        email: currentUser?.email || undefined,
      },
    );
  }, []);

  const changeRole = useCallback(async (email: string, role: UserRole) => {
    const currentUser = auth.currentUser;
    await updateStaffAccess(
      email,
      { role },
      {
        displayName: currentUser?.displayName || undefined,
        email: currentUser?.email || undefined,
      },
    );
  }, []);

  const bulkUpdate = useCallback(
    async (emails: string[], updates: { hasAccess?: boolean; role?: UserRole }) => {
      const currentUser = auth.currentUser;
      await bulkUpdateStaffAccess(emails, updates, {
        displayName: currentUser?.displayName || undefined,
        email: currentUser?.email || undefined,
      });
    },
    [],
  );

  const annotatedAll = annotateStaffWithAccess(rawStaff, staffAccess);
  const activeRfp = annotatedAll.filter((m) => m.hasAccess === true);
  const leads = activeRfp.filter((m) => m.role === "admin" || m.role === "lead");

  return {
    allStaff: annotatedAll,
    activeRfpStaff: activeRfp,
    leads,
    loading,
    refreshing,
    error,
    refreshStaff,
    toggleAccess,
    changeRole,
    bulkUpdate,
  };
}
